/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import type { BoardQuery } from './__generated__/BoardQuery.graphql.ts';

export type HydratedBoard = { name: string; url: string | null };

type NodeResponse =
	| { __typename: 'JiraProject'; projectName: string; webUrl?: string | null }
	| { __typename: 'JiraBoard'; boardName?: string | null; boardUrl?: string | null }
	| null;

function normalizeBoardFromNode(node: NodeResponse): HydratedBoard | null {
	if (!node) {
		return null;
	}
	if (node.__typename === 'JiraProject') {
		return { name: node.projectName, url: node.webUrl ?? null };
	}
	if (node.__typename === 'JiraBoard') {
		const name = node.boardName ?? null;
		if (name) {
			return { name, url: node.boardUrl ?? null };
		}
	}
	return null;
}

type BoardQueryProps = {
	scopeId: string;
	children: (board: HydratedBoard | null) => React.ReactNode;
};

export function BoardQuery({ scopeId, children }: BoardQueryProps): React.ReactNode {
	const data = useLazyLoadQuery<BoardQuery>(
		graphql`
			query BoardQuery($id: ID!) {
				node(id: $id) {
					__typename
					... on JiraProject {
						projectName: name
						webUrl
					}
					... on JiraBoard {
						boardName: name
						boardUrl
					}
				}
			}
		`,
		{ id: scopeId },
		{ fetchPolicy: 'store-or-network' },
	);

	const board = useMemo(() => normalizeBoardFromNode(data.node as NodeResponse), [data.node]);

	return children(board);
}
